// Per-client DME — every order placed for them, and the authorization each
// order carries. CareSource authorizes DME per order, so the order is where an
// authorization lives (and what blocks resupply when it lapses); the client
// record's dme_auth_* columns were a second copy nothing used, and this card
// read them (retired 2026-10-19). Ordering and fulfilment happen on the DME pages;
// this is the client-side view of them, so a supplies client's record is not
// silent about the only service they actually receive.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, differenceInDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { STATUS_PIPELINE, SIDE_STATUSES, money } from '../../lib/dmeWorkflow';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '6px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const row = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 12.5 };
const lbl = { color: 'var(--text3)' };

const fmt = (d) => { try { return d ? format(parseISO(String(d)), 'MMM d, yyyy') : '—'; } catch { return '—'; } };

const STATUS_LABEL = Object.fromEntries([...STATUS_PIPELINE, ...SIDE_STATUSES].map(p => [p.key, p.label]));

export default function ClientDmeTab({ client }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('dme_orders')
      .select('id, order_ref, status, product_category, product_description, hcpcs_code, quantity_ordered, quantity_unit, supplier_name, order_date, delivered_date, expected_amount, claim_amount, auth_number, auth_start_date, auth_end_date, is_recurring, next_resupply_date, resupply_blocked_reason, parent_order_id')
      .eq('client_id', client.id)
      .eq('is_test', false)
      .order('order_date', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  // One row per authorization number across the client's orders.
  const auths = Object.values(orders.reduce((by, o) => {
    if (!o.auth_number) return by;
    const k = String(o.auth_number).trim();
    const e = by[k] || { number: k, start: o.auth_start_date, end: o.auth_end_date, items: [] };
    // A resupply repeats its parent's item; list each item once.
    const item = o.product_description || o.product_category || o.hcpcs_code || 'Order';
    if (!e.items.includes(item)) e.items.push(item);
    if (o.auth_end_date && (!e.end || o.auth_end_date > e.end)) e.end = o.auth_end_date;
    by[k] = e;
    return by;
  }, {}));
  const daysLeft = (d) => (d ? differenceInDays(parseISO(String(d)), new Date()) : null);
  const tone = (n) => n === null ? 'var(--text3)' : n < 0 ? 'var(--danger)' : n <= 60 ? 'var(--warn)' : 'var(--success)';

  return (
    <>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>DME Authorization</h3>
          <Link to={`/dme/orders?client=${client.id}`} className="btn btn-sm"
            style={{ background: 'var(--accent)', color: '#fff', borderRadius: 6, padding: '7px 12px', textDecoration: 'none', fontSize: 12 }}>
            + New order
          </Link>
        </div>
        <div style={{ marginTop: 10 }}>
          {loading ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
            : auths.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No authorization on any order yet. DME is authorized per order, on the order.</div>
            : auths.map(a => {
              const n = daysLeft(a.end);
              return (
                <div key={a.number} style={{ ...row, flexWrap: 'wrap' }}>
                  <span><b>{a.number}</b> <span style={lbl}>· {a.items.join(', ')}</span></span>
                  <span style={{ color: tone(n), fontWeight: n !== null && n <= 60 ? 600 : 400 }}>
                    {a.start ? `${fmt(a.start)} – ` : 'to '}{fmt(a.end)}
                    {n !== null && (n < 0 ? ' — EXPIRED' : ` · ${n}d left`)}
                  </span>
                </div>
              );
            })}
        </div>
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 10px' }}>Orders</h3>
        {loading ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
          : orders.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No DME orders for this client yet.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr>
                    <th style={th}>Order</th><th style={th}>Item</th><th style={th}>Qty</th>
                    <th style={th}>Expected</th><th style={th}>Ordered</th>
                    <th style={th}>Status</th><th style={th}>Resupply</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td style={td}>{o.order_ref || '—'}</td>
                      <td style={td}>
                        {o.product_description || o.product_category || '—'}
                        {o.hcpcs_code && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{o.hcpcs_code}</div>}
                      </td>
                      <td style={td}>{o.quantity_ordered ? `${o.quantity_ordered} ${o.quantity_unit || ''}`.trim() : '—'}</td>
                      <td style={td}>{money(o.claim_amount ?? o.expected_amount)}</td>
                      <td style={td}>{fmt(o.order_date)}{o.parent_order_id ? <div style={{ fontSize: 10, color: 'var(--text3)' }}>resupply</div> : null}</td>
                      <td style={td}>{STATUS_LABEL[o.status] || o.status || '—'}</td>
                      <td style={td}>
                        {o.resupply_blocked_reason
                          ? <span style={{ color: 'var(--danger)' }} title={o.resupply_blocked_reason}>⚠ refused</span>
                          : o.next_resupply_date ? `due ${fmt(o.next_resupply_date)}` : o.is_recurring ? 'after delivery' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </>
  );
}
